import type { DownloadAsset } from "../api/accountApi";

export type OsFamily = "windows" | "mac" | "other";

export function detectOs(): OsFamily {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return "windows";
  if (/Mac/i.test(platform) || /Mac OS|Macintosh/i.test(ua)) return "mac";
  return "other";
}

export function formatBytes(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toLocaleString("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}\u00a0MB`;
}

export function formatPublished(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("de-DE", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function installerMeta(
  asset: DownloadAsset,
  publishedAt: string | null | undefined,
): string {
  return [
    asset.version ? `Version ${asset.version}` : null,
    formatPublished(publishedAt),
    formatBytes(asset.size_bytes),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Installer a person runs. Updater archives stay hidden once an exe, msi or dmg exists. */
export function visibleInstallers(assets: DownloadAsset[]): DownloadAsset[] {
  const installers = assets.filter((asset) => {
    const name = asset.filename.toLowerCase();
    return name.endsWith(".exe") || name.endsWith(".msi") || name.endsWith(".dmg");
  });
  const source =
    installers.length > 0
      ? installers
      : assets.filter((asset) => !asset.filename.toLowerCase().endsWith(".sig"));
  const seen = new Set<string>();
  const out: DownloadAsset[] = [];
  for (const asset of source) {
    if (seen.has(asset.filename)) continue;
    seen.add(asset.filename);
    out.push(asset);
  }
  return out;
}

function rankAsset(asset: DownloadAsset, os: OsFamily): number {
  const name = asset.filename.toLowerCase();
  if (os === "windows") {
    const win = asset.platform.startsWith("windows") || name.endsWith(".exe") || name.endsWith(".msi");
    if (!win) return 10;
    if (name.endsWith("-setup.exe")) return 0;
    if (name.endsWith(".exe")) return 1;
    if (name.endsWith(".msi")) return 2;
    return 3;
  }
  const mac = asset.platform.startsWith("darwin") || name.endsWith(".dmg") || name.endsWith(".app.tar.gz");
  if (!mac) return 10;
  if (name.endsWith(".dmg")) return 0;
  if (asset.platform === "darwin-universal") return 1;
  if (name.endsWith(".app.tar.gz")) return 6;
  return 3;
}

export function bestInstaller(assets: DownloadAsset[], os: OsFamily): DownloadAsset | null {
  const list = visibleInstallers(assets);
  let best: { asset: DownloadAsset; rank: number } | null = null;
  for (const asset of list) {
    const rank = rankAsset(asset, os);
    if (rank >= 10) continue;
    if (!best || rank < best.rank) best = { asset, rank };
  }
  return best?.asset ?? null;
}
